import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password must be no more than 128 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [token, setToken] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    // Get token and email from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get("token");
    const emailParam = urlParams.get("email");
    
    if (!tokenParam || !emailParam) {
      toast({
        title: "Invalid Reset Link",
        description: "This password reset link is invalid or has expired.",
        variant: "destructive",
      });
      setLocation("/forgot-password");
      return;
    }
    
    setToken(tokenParam);
    setEmail(emailParam);
  }, [setLocation, toast]);

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: ""
    },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token || !email) return;
    
    setIsSubmitting(true);
    try {
      const response = await apiRequest("POST", "/auth/reset-password", {
        token,
        email,
        password: data.password
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast({
          title: "Password Reset Successful",
          description: "Your password has been reset. You can now log in with your new password.",
        });
        setLocation("/login");
      } else {
        // Display server-side validation errors if available
        const errorMessage = result.message || "Failed to reset password";
        const errors = result.errors && Array.isArray(result.errors) ? result.errors : [];
        
        // Format errors as a list for better readability
        const errorDescription = errors.length > 0 ? (
          <div className="space-y-1">
            <p className="font-medium">{errorMessage}</p>
            <ul className="list-disc list-inside text-sm space-y-0.5 ml-2">
              {errors.map((err: string, index: number) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </div>
        ) : errorMessage;
        
        toast({
          title: "Password Validation Error",
          description: errorDescription,
          variant: "destructive",
        });
        return;
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token || !email) {
    return null; // Don't render until we have the required parameters
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 font-muli">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 relative">
        <CardHeader className="text-center space-y-2 pt-16">
          {/* Back Button */}
          <div className="absolute top-4 left-4">
            <Button
              variant="ghost"
              onClick={() => setLocation("/login")}
              className="text-servify-purple hover:text-servify-purple-dark hover:bg-servify-purple/10"
            >
              ← Back to Login
            </Button>
          </div>
          <CardTitle className="text-3xl font-bold text-servify-purple">
            Reset Password
          </CardTitle>
          <CardDescription className="text-gray-600">
            Enter your new password for {email}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium">New Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Enter your new password"
                        className="h-12 border-gray-300 focus:border-servify-purple focus:ring-servify-purple/20"
                      />
                    </FormControl>
                    <FormMessage />
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      <p className="font-semibold">Password requirements:</p>
                      <ul className="list-disc list-inside space-y-0.5 ml-2">
                        <li>At least 8 characters long</li>
                        <li>One uppercase letter</li>
                        <li>One lowercase letter</li>
                        <li>One number</li>
                        <li>One special character (!@#$%^&*()_+-=[]{}|;:,.&lt;&gt;?)</li>
                        <li>Cannot reuse your last 5 passwords</li>
                      </ul>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium">Confirm New Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Confirm your new password"
                        className="h-12 border-gray-300 focus:border-servify-purple focus:ring-servify-purple/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-servify-purple hover:bg-servify-purple-dark text-white font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Remember your password?{" "}
              <button 
                onClick={() => setLocation("/login")}
                className="text-servify-purple hover:text-servify-purple-dark font-medium underline hover:no-underline transition-colors"
              >
                Back to Login
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
